import { AUTH_CONSENT } from '@dmi/shared/redux/Main/constants';

import { CONSENT_MOBILE_ALLOWED_PATHS, CONSENT_PATH } from './constants';

/**
 * @description Handles navigation redirects related to user consent based on the current
 * path, session state, and whether the user is using SSO authentication.
 *
 * @param {string} currentPath - The base path the user is attempting to access,
 * extracted from the full path (e.g. if accessing '/login/consent', currentPath is '/login')
 * @param {boolean} isLoggedIn - Whether the user is fully authenticated and logged in
 * @param {boolean} isSsoUser - Whether the user is authenticated via SSO
 * @param {string} path - The complete path being navigated to
 * (e.g. if accessing '/login/consent', path is '/login/consent')
 * @param {string} sessionState - The current authentication state (AUTH_CONSENT, AUTH_FULL, etc.)
 * @returns {string} The path to redirect to
 */
export const getPathBasedOnConsentState = ({
  currentPath,
  isLoggedIn,
  isSsoUser,
  path,
  sessionState,
}) => {
  // Partially authenticated SSO user requiring consent -> redirect to consent page
  if (sessionState === AUTH_CONSENT && isSsoUser) {
    return CONSENT_PATH;
  }

  /**
   * User attempting to access consent page while not in a state requiring
   * consent -> redirect to appropriate landing page
   */
  if (path === CONSENT_PATH && sessionState !== AUTH_CONSENT) {
    return isLoggedIn ? '/payments' : '/login';
  }

  /**
   * If user needs to complete consent and tries to access a restricted path,
   * redirect to consent page. Only allow access to specific pre-auth paths
   * (login, register, etc.) while consent is pending. Also allow access to the
   * root path '/' (which itself will redirect to /login) while consent is pending.
   */
  if (sessionState === AUTH_CONSENT &&
      !CONSENT_MOBILE_ALLOWED_PATHS.includes(currentPath) &&
      path !== '/') {
    return CONSENT_PATH;
  }

  // No redirect needed - user can proceed to requested path
  return path;
};
